<template>
  <section>
    <section class="page-stack grid-stack grid-gap-page-section">
      <section
        v-if="state.isLoading"
        class="page-panel grid-stack grid-gap-12 justify-self-center text-center"
      >
        <v-progress-circular indeterminate />
        <p class="panel-description">Učitavanje aktivnosti...</p>
      </section>

      <section
        v-else-if="state.loadErrorMessage"
        class="form page-panel grid-stack grid-gap-20"
      >
        <p class="notice" role="alert">{{ state.loadErrorMessage }}</p>

        <v-btn
          class="button button--secondary button--small justify-self-start"
          :to="backRoute"
          variant="outlined"
        >
          Natrag na profil
        </v-btn>
      </section>

      <v-form
        v-else
        ref="activityFormRef"
        class="form body-only-form"
        @submit.prevent="saveActivityAsync"
      >
        <section class="body-only-form__header grid-stack grid-gap-8">
          <router-link
            class="back-link"
            :to="backRoute"
            aria-label="Natrag"
          >
            <v-icon
              icon="mdi-arrow-left"
              size="20"
            />
            <span>Natrag</span>
          </router-link>

          <header>
            <h2 class="h3">{{ activityName }}</h2>
            <p class="panel-description">Detalji aktivnosti</p>
          </header>
        </section>

        <div class="body-only-form__content grid-stack grid-gap-20">
          <section
            v-if="isTestDrive"
            class="grid-stack grid-gap-12"
          >
            <h3 class="panel-title">Informacije o vozilu</h3>

            <AppSelect
              v-if="!isFinishingTestDrive"
              v-model="activityFormValues.vehicleId"
              :items="vehicleOptions"
              label="Vozilo"
              placeholder="Odaberite vozilo"
              required
              @update:model-value="setOfflineVehicleWarning"
            />
            <AppTextField
              v-else
              label="Vozilo"
              :model-value="activeVehicleDisplayName"
              readonly
            />
          </section>

          <section class="grid-stack grid-gap-12">
            <h3 class="panel-title">
              <v-icon
                icon="mdi-clock-outline"
                size="18"
              />
              {{ isFinishingTestDrive ? 'Završetak' : 'Početak' }}
            </h3>

            <AppDateTimeField
              v-model="activityFormValues.dateTime"
              label="Datum i vrijeme"
              required
            />
          </section>

          <section class="grid-stack grid-gap-12">
            <h3 class="panel-title">Komentar</h3>

            <AppTextarea
              v-model="activityFormValues.comment"
              label="Dodaj komentar"
              :maxlength="1000"
              :rows="3"
            />
          </section>

          <p
            v-if="isTestDrive"
            class="notice"
          >
            Aktivnost se označava kao završena kada su uneseni početno i završno vrijeme.
          </p>
        </div>

        <div class="body-only-form__actions">
          <ToolbarActionButton
            class="body-only-form__button"
            :disabled="state.isSaving"
            icon=""
            :loading="state.isSaving"
            text="Prihvati"
            type="submit"
          />
        </div>
      </v-form>
    </section>

    <AppConfirmDialog
      v-model="state.vehicleConflictDialogOpen"
      title="Vozilo je zauzeto"
      :subtitle="selectedVehiclePlate"
      :message="vehicleConflictMessage"
      primary-text="Dodijeli vozilo"
      secondary-text="Odustani"
      @confirm="saveActivityMutationAsync(state.conflictingTestDrive)"
    />

    <AppConfirmDialog
      v-model="state.offlineVehicleWarningDialogOpen"
      title="Uređaj je izvan mreže"
      message="Nije moguće provjeriti je li odabrano vozilo zauzeto. Prije početka vožnje provjerite je li vozilo slobodno."
      primary-text="U redu"
      variant="blocked"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppConfirmDialog from '../../components/AppConfirmDialog.vue'
import AppDateTimeField from '../../components/AppDateTimeField.vue'
import AppSelect from '../../components/AppSelect.vue'
import AppTextarea from '../../components/AppTextarea.vue'
import AppTextField from '../../components/AppTextField.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import {
  formatDateTimeInputValue,
  formatIsoDateTime,
  getVehicleDisplayName,
  getVehicleHandoverTimestamp,
} from '../../helpers'
import { ApiError } from '../../services/apiClient'
import {
  getActivityEventAsync,
  saveInviteeActivityAsync,
} from '../../services/offlineService'
import { appStore, showErrorToast, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
const activityFormRef = ref(null)
const state = reactive({
  event: null,
  invitee: null,
  activity: null,
  activeActivity: null,
  conflictingTestDrive: null,
  isLoading: true,
  isSaving: false,
  loadErrorMessage: '',
  offlineVehicleWarningDialogOpen: false,
  vehicleConflictDialogOpen: false,
})
const activityFormValues = reactive({
  vehicleId: null,
  dateTime: '',
  comment: '',
})

const backRoute = computed(() => ({
  name: 'event-invitee-detail',
  params: {
    eventId: route.params.eventId,
    inviteeId: route.params.inviteeId,
  },
  query: route.query,
}))
const isTestDrive = computed(() => state.activity.activity.type === 'test-drive')
const isFinishingTestDrive = computed(() => Boolean(state.activeActivity))
const activityName = computed(() => state.activity.activity.customFieldValue
  ? `${state.activity.activity.name}: ${state.activity.activity.customFieldValue}`
  : state.activity.activity.name)
const vehicleOptions = computed(() => state.event.vehicles.map((vehicle) => ({
  title: getVehicleDisplayName(vehicle),
  value: vehicle.id,
})))
const activeVehicleDisplayName = computed(() => {
  const activeVehicle = state.event.vehicles.find((vehicle) => vehicle.id === state.activeActivity.vehicleId)
  return activeVehicle ? getVehicleDisplayName(activeVehicle) : state.activeActivity.vehiclePlate
})
const selectedVehiclePlate = computed(() => state.event?.vehicles.find(
  (vehicle) => vehicle.id === activityFormValues.vehicleId
)?.plate ?? '')
const vehicleConflictMessage = computed(() => state.conflictingTestDrive
  ? `Ovo vozilo ima aktivnost u tijeku s korisnikom ${state.conflictingTestDrive.inviteeName}. Ako dodijelite ovo vozilo novom uzvaniku, ta aktivnost će automatski biti završena.`
  : '')

onMounted(getActivityTrackingDataAsync)

async function getActivityTrackingDataAsync () {
  state.isLoading = true
  state.loadErrorMessage = ''

  try {
    const event = await getActivityEventAsync(route.params.eventId)
    const invitee = event.invitees.find(({ id }) => id === route.params.inviteeId)
    const eventActivity = event.activities.find(({ id }) => id === route.params.activityTypeId)

    if (!invitee || !eventActivity) {
      throw new Error('The invitee or activity is not assigned to this event.')
    }

    const activityRecords = invitee.activities.filter((activity) => (
      (activity.activityTypeId ?? activity.id) === route.params.activityTypeId
    ))
    const activeActivity = activityRecords.find(({ startedAt, finishedAt }) => startedAt && !finishedAt)
    const pendingActivity = {
      id: eventActivity.id,
      eventInviteeId: invitee.id,
      activityTypeId: eventActivity.id,
      activity: eventActivity,
    }
    const activity = activeActivity ??
      activityRecords.find(({ startedAt }) => !startedAt) ??
      activityRecords[0] ?? pendingActivity

    if (event.isArchived) {
      throw new Error('Activities cannot be tracked for an archived event.')
    }

    if (activity.activity.type !== 'test-drive' && activityRecords.some(({ startedAt }) => startedAt)) {
      showErrorToast('Ova aktivnost je već evidentirana.')
      await router.replace(backRoute.value)
      return
    }

    state.event = event
    state.invitee = invitee
    state.activity = activity
    state.activeActivity = activeActivity ?? null
    activityFormValues.dateTime = formatDateTimeInputValue()
  } catch (error) {
    console.error('Failed to load invitee activity tracking data.', error)
    state.loadErrorMessage = 'Aktivnost nije bilo moguće učitati.'
    showRequestErrorToast(error, state.loadErrorMessage)
  } finally {
    state.isLoading = false
  }
}

async function saveActivityAsync () {
  if (state.isSaving) {
    return
  }

  const { valid } = await activityFormRef.value.validate()

  if (!valid) {
    return
  }

  const cachedVehicleConflict = !appStore.isOnline &&
    isTestDrive.value &&
    !isFinishingTestDrive.value
    ? getVehicleConflict()
    : null

  if (cachedVehicleConflict) {
    state.conflictingTestDrive = cachedVehicleConflict
    state.vehicleConflictDialogOpen = true
    return
  }

  await saveActivityMutationAsync()
}

function getVehicleConflict () {
  for (const invitee of state.event.invitees) {
    const activeTestDrive = invitee.activities.find(({ activity, finishedAt, startedAt, vehicleId }) => (
      activity.type === 'test-drive' &&
      vehicleId === activityFormValues.vehicleId &&
      startedAt &&
      !finishedAt
    ))

    if (activeTestDrive) {
      return {
        ...activeTestDrive,
        eventInviteeId: invitee.id,
        inviteeName: invitee.fullName,
      }
    }
  }

  return null
}

function setOfflineVehicleWarning (vehicleId) {
  if (vehicleId && !appStore.isOnline) {
    state.offlineVehicleWarningDialogOpen = true
  }
}

async function saveActivityMutationAsync (conflictingTestDrive = null) {
  if (state.isSaving) {
    return
  }

  const requestedTimestamp = formatIsoDateTime(activityFormValues.dateTime)
  const timestamp = conflictingTestDrive
    ? getVehicleHandoverTimestamp(
      conflictingTestDrive.startedAt,
      requestedTimestamp,
      new Date()
    )
    : requestedTimestamp

  if (!timestamp) {
    showErrorToast('Vrijeme početka nove vožnje mora biti nakon početka aktivne vožnje.')
    return
  }

  state.isSaving = true

  try {
    const comment = activityFormValues.comment.trim() || null
    const result = await saveInviteeActivityAsync({
      activity: state.activity,
      activeActivity: state.activeActivity,
      comment,
      conflictingTestDrive,
      event: state.event,
      invitee: state.invitee,
      timestamp,
      vehicleId: activityFormValues.vehicleId,
    })

    showSuccessToast(result.queued
      ? 'Aktivnost je spremljena na uređaju i sinkronizirat će se kada veza bude dostupna.'
      : isFinishingTestDrive.value
        ? 'Aktivnost je uspješno završena.'
        : isTestDrive.value
          ? 'Aktivnost je uspješno pokrenuta.'
          : 'Aktivnost je uspješno evidentirana.')
    await router.push(backRoute.value)
  } catch (error) {
    console.error('Failed to save the invitee activity.', error)

    const apiConflict = await getApiVehicleConflictAsync(error)
    if (apiConflict) {
      state.conflictingTestDrive = apiConflict
      state.vehicleConflictDialogOpen = true
      return
    }

    showRequestErrorToast(error, 'Aktivnost nije bilo moguće spremiti.')
  } finally {
    state.isSaving = false
  }
}

async function getApiVehicleConflictAsync (error) {
  if (
    !(error instanceof ApiError) ||
    error.status !== 409 ||
    error.data?.error !== 'vehicle_conflict'
  ) {
    return null
  }

  try {
    state.event = await getActivityEventAsync(route.params.eventId)

    return getVehicleConflict()
  } catch (loadError) {
    console.error('Failed to load vehicle conflict data.', loadError)
    return null
  }
}

</script>
